import React, { Component, type ReactElement } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import type { UIAnalyticsEvent as AnalyticsEvent } from '@atlaskit/analytics-next';
import { FilterBar } from '@atlassian/jira-common-directory-v2/src/components/filter-bar/FilterBar.tsx';
import { FilterItem } from '@atlassian/jira-common-directory-v2/src/components/filter-bar/FilterItem.tsx';
import type {
	Project,
	Group,
	Owner,
	Filter as FiltersType,
} from '@atlassian/jira-common-directory-v2/src/model/shareable-entities/index.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { Intl } from '@atlassian/jira-shared-types/src/general.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import messages from './messages.tsx';

type OnSearchChange = (arg1: string, arg2: AnalyticsEvent) => void;
type OnOwnerChange = (owner: Owner, event: AnalyticsEvent) => void;
type OnProjectChange = (project: Project, event: AnalyticsEvent) => void;
type OnGroupChange = (group: Group, event: AnalyticsEvent) => void;
type OnClear = (event: AnalyticsEvent) => void;

export type Props = {
	renderFilterSearch: (arg1: { onChange: OnSearchChange; placeholder: string }) => ReactElement;
	renderOwnerPicker: (arg1: {
		onChange: OnOwnerChange;
		placeholder: string;
		label?: string;
	}) => ReactElement;
	renderProjectPicker: (arg1: {
		onChange: OnProjectChange;
		placeholder: string;
		label?: string;
	}) => ReactElement;
	renderGroupPicker: (arg1: {
		onChange: OnGroupChange;
		placeholder: string;
		label?: string;
	}) => ReactElement;
	renderClear: (arg1: { onClick: OnClear }) => ReactElement;
	initialFilter: FiltersType;
	value: FiltersType;
} & Intl & {
		onChange: (filters: FiltersType, event: AnalyticsEvent) => void;
	};

// eslint-disable-next-line jira/react/no-class-components
export class Filter extends Component<Props> {
	// @ts-expect-error - TS2322 - Type 'undefined' is not assignable to type 'string'.
	onSearchChange: OnSearchChange = (text = undefined, analyticsEvent) => {
		this.props.onChange({ ...this.props.value, text }, analyticsEvent);
	};

	onOwnerChange: OnOwnerChange = (owner, analyticsEvent) => {
		this.props.onChange({ ...this.props.value, owner }, analyticsEvent);
	};

	onProjectChange: OnProjectChange = (project, analyticsEvent) => {
		this.props.onChange({ ...this.props.value, project }, analyticsEvent);
	};

	onGroupChange: OnGroupChange = (group, analyticsEvent) => {
		this.props.onChange({ ...this.props.value, group }, analyticsEvent);
	};

	onClear: OnClear = (analyticsEvent) => {
		this.props.onChange({ ...this.props.initialFilter }, analyticsEvent);
	};

	render(): React.JSX.Element {
		const {
			renderFilterSearch,
			renderOwnerPicker,
			renderProjectPicker,
			renderGroupPicker,
			renderClear,
			intl: { formatMessage },
		} = this.props;

		const isVisualRefresh = isVisualRefreshEnabled();

		const projectLabelGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
			? messages.projectLabelGalaxia
			: messages.projectLabel;
		const projectPlaceholderGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
			? messages.projectPlaceholderGalaxia
			: messages.projectPlaceholder;

		return (
			<FilterBar>
				<FilterItem expandOnSmall>
					{renderFilterSearch({
						onChange: this.onSearchChange,
						placeholder: formatMessage(messages.searchPlaceholder),
					})}
				</FilterItem>
				<FilterItem>
					<Box xcss={!isVisualRefresh && itemInnerStyles}>
						{renderOwnerPicker({
							onChange: this.onOwnerChange,
							placeholder: isVisualRefresh
								? formatMessage(messages.ownerLabel)
								: formatMessage(messages.ownerPlaceholder),
							label: isVisualRefresh ? undefined : formatMessage(messages.ownerLabel),
						})}
					</Box>
				</FilterItem>
				<FilterItem>
					<Box xcss={!isVisualRefresh && itemInnerStyles}>
						{renderProjectPicker({
							onChange: this.onProjectChange,
							placeholder: isVisualRefresh
								? formatMessage(projectLabelGalaxia)
								: formatMessage(projectPlaceholderGalaxia),
							label: isVisualRefresh ? undefined : formatMessage(messages.projectLabel),
						})}
					</Box>
				</FilterItem>
				<FilterItem>
					<Box xcss={!isVisualRefresh && itemInnerStyles}>
						{renderGroupPicker({
							onChange: this.onGroupChange,
							placeholder: isVisualRefresh
								? formatMessage(messages.groupLabel)
								: formatMessage(messages.groupPlaceholder),
							label: isVisualRefresh ? undefined : formatMessage(messages.groupLabel),
						})}
					</Box>
				</FilterItem>
				<FilterItem expandOnSmall>{renderClear({ onClick: this.onClear })}</FilterItem>
			</FilterBar>
		);
	}
}

export default injectIntl(Filter);

const itemInnerStyles = xcss({
	marginTop: 'space.negative.250',
});
